export enum CompanyStatus {
  ACTIVE = "active",
  INACTIVE = "inactive", 
  DELETED = "deleted",
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  TRIAL = 'trial',
}

export interface PaymentRecord {
  id: string;
  purchasedLimit: number;
  amountPaid: number;
  currency: string;
  startDate: string;
  expiryDate: string;
  type: "trial" | "premium";
  paymentPlan?: "monthly" | "annually" | "trial";
  durationDays: number;
  couponCode?: string;
  paymentStatus: "pending" | "paid" | "failed";
  description?: string;
  paidAt?: string;
  iyzicoSubscriptionReferenceCode?: string;
}

export interface CompanyPaymentRecord {
  id: string;
  companyName: string;
  companyId: number;
  planType: string;
  amount: number;
  currency: string;
  paymentStatus: string;
  paymentDate: string;
  expiryDate: string;
  subscriptionType: string;
  userLimit: number;
  description?: string;
}

export interface CompanySettings {
  maxDailyWorkHours?: number;
  lateTolerance: boolean;
  lateTolerationMinutes: number;
  ipControl: boolean;
  annualLeaveLimit: number;
  shiftMode: 'assignment' | 'fixed' | 'both';
  useFixedShift: boolean;
  fixedShiftDays: string[];
  fixedShiftStartTime: string;
  fixedShiftEndTime: string;
  sendMailLeave: boolean;
  sendMailTicket: boolean;
  sendMailExcuse: boolean;
  sendNotifLeave: boolean;
  sendNotifTicket: boolean;
  sendNotifExcuse: boolean;
  sendNotifBreak: boolean;
  defaultBreakDuration: number;
  maxBreakDuration?: number;
  userLimit: number;
  hasUsedTrial: boolean;
  sendSmsLeave: boolean;
  sendSmsTicket: boolean;
  sendSmsExcuse: boolean;
  sendSmsBreak: boolean;
}

export interface Company {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  city?: string;
  zipCode?: string;
  taxNumber: string;
  taxOffice?: string;
  iyzicoCustomerReferenceCode?: string;
  subscriptionStatus: SubscriptionStatus;
  gracePeriodEndsAt: Date | null;
  subscriptionReferenceCode: string | null;
  subscriptionType: 'monthly' | 'annually' | null;
  settings?: CompanySettings;
  paymentRecords?: PaymentRecord[];
  status: CompanyStatus;
  users?: any[];
  branches?: any[];
  departments?: any[];
  isDeleted: boolean;
  onboardingSecret?: string;
  onboardingExpiresAt?: Date;
  onboardingUsed: boolean;
  createdAt: Date;
  updatedAt: Date;
  billingAddress?: BillingAddress;
}

export interface BillingAddress {
  alias: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  contactName?: string;
  taxNumber?: string;
  taxOffice?: string;
}

export interface CompanySubscriptionStatusInfo {
  expired: boolean;
  type: "trial" | "premium" | null;
  daysLeft: number;
  userLimit: number;
  message: string;
}



export interface TopPayingCompany {
  companyName: string;
  totalPaid: number;
  lastPayment: string;
  planType: string;
  transactionCount: number;
}

export interface PaymentStats {
  totalRevenue: number;
  monthlyRevenue: number;
  totalTransactions: number;
  activeSubscriptions: number;
  trialUsers: number;
  canceledSubscriptions: number;
  averageRevenuePerUser: number;
  averageOrderValue?: number;
  revenueGrowth: number;
  topPayingCompanies: TopPayingCompany[];
  monthlyRevenueData: Array<{
    month: string;
    revenue: number;
    transactions: number;
  }>;
  paymentStatusDistribution: {
    paid: number;
    pending: number;
    failed: number;
    refunded: number;
  };
  planTypeDistribution: {
    premium: number;
    basic: number;
    enterprise: number;
  };
}

export interface PaymentFilters {
  paymentStatus?: string;
  planType?: string;
  dateRange?: [string, string];
  companyName?: string;
}

export interface UpdateCompanySettingsDTO {
  settings: CompanySettings;
}

export interface AnnualLeaveLimitResponse {
  annualLeaveLimit: number;
}

export interface CreateCompanyDto {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  taxNumber: string;
  taxOffice: string;
  defaultBreakDuration: number;
  status: CompanyStatus;
  settings?: CompanySettings;
}

export interface UpdateCompanyDto {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  taxNumber?: string;
  taxOffice?: string;
  defaultBreakDuration?: number;
  status?: CompanyStatus;
  settings?: CompanySettings;
}

export interface SuperSettings {
  id: number;
  name: string;
  settings: CompanySettings;
}

export interface AboutUs {
  content: string;
}